import { productApi } from "#/api/productApi";
import ActionButton from "#/components/buttons/ActionButton";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { ProductRotation } from "#/types/subClient";
import { Accordion, NumberInput, Select } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  rotations: ProductRotation[];
  onChange: (rotations: ProductRotation[]) => void;
}

const RotationAccordion = ({ rotations, onChange }: Props) => {
  const { t } = useTranslation();
  const { canWrite, canDelete } = usePermission();

  const { data: products = [], isPending: productsPending } = useQuery({
    queryKey: ["products-for-rotations"],
    queryFn: async () => {
      const res = await productApi.getAll({ limit: 1000, page: 1 });
      return res.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const productOptions = products.map((p) => ({
    value: String(p.id),
    label: p.name ?? `#${p.id}`,
  }));

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([...rotations, { productId: 0, days: 0, count: 0 }]);
  };

  const updateRow = (index: number, patch: Partial<ProductRotation>) => {
    const next = rotations.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    onChange(rotations.filter((_, i) => i !== index));
  };

  return (
    <Accordion.Item value="rotations">
      <Accordion.Control>
        <div className="flex items-center gap-2">
          <span>{t("subClients.sections.rotations")}</span>
          <PermissionTooltip disabled={!canWrite()}>
            <ActionButton
              color="black"
              size="xs"
              icon={IconPlus}
              disabled={!canWrite()}
              onClick={handleAdd}
            />
          </PermissionTooltip>
        </div>
      </Accordion.Control>
      <Accordion.Panel>
        <div className="text-sm text-gray-600 mb-3">
          {t("subClients.sections.rotationsHint")}
        </div>
        {rotations.length === 0 ? (
          <div className="text-sm text-gray-500">
            {t("subClients.rotations.empty")}
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            {rotations.map((rotation, index) => (
              <div
                key={index}
                className="w-full grid grid-cols-[auto_1fr_160px_160px_auto] gap-2 items-end"
              >
                <span className="bg-black/5 font-bold text-black/70 rounded-full w-8 h-8 flex items-center justify-center">
                  {index + 1}
                </span>

                <Select
                  label={t("subClients.rotations.category")}
                  placeholder={t("subClients.rotations.categoryPlaceholder")}
                  data={productOptions}
                  value={rotation.productId ? String(rotation.productId) : null}
                  searchable
                  disabled={!canWrite() || productsPending}
                  onChange={(val) => {
                    const id = val ? Number(val) : 0;
                    const product = products.find((p) => p.id === id);
                    updateRow(index, {
                      productId: id,
                      productName: product?.name,
                    });
                  }}
                />

                <NumberInput
                  label={t("subClients.rotations.days")}
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  disabled={!canWrite()}
                  value={rotation.days}
                  onChange={(val) => {
                    updateRow(index, { days: typeof val === "number" ? val : 0 });
                  }}
                />

                <NumberInput
                  label={t("subClients.rotations.count")}
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  disabled={!canWrite()}
                  value={rotation.count}
                  onChange={(val) => {
                    updateRow(index, { count: typeof val === "number" ? val : 0 });
                  }}
                />

                <PermissionTooltip disabled={!canDelete()}>
                  <ActionButton
                    icon={IconTrash}
                    variant="white"
                    color="red.5"
                    disabled={!canDelete()}
                    onClick={() => removeRow(index)}
                  />
                </PermissionTooltip>
              </div>
            ))}
          </div>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default RotationAccordion;
